import { useState, useMemo } from "react";
import {
  Collapse,
  Typography,
  Descriptions,
  Tag,
  Table,
  Empty,
  Button,
  Popconfirm,
  Space,
  Spin,
  Alert,
  Tooltip,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  ToolOutlined,
  EyeOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { motion } from "framer-motion";
import { useParams } from "react-router-dom";
import { usePermission } from "../hooks/usePermission";
import { PERMISSIONS } from "../constants/permissions";
import { colors, shadows } from "../styles/theme";
import { useAppSelector, useAppDispatch } from "../store/hooks";
import { selectAuth } from "../store/authSlice";
import { useGetPOByIdQuery, useUpdatePOMutation } from "../store/api/poApi";
import {
  useGetDispatchesByPoIdQuery,
  useDeleteDispatchMutation,
  useUpdateDispatchDocumentsMutation,
} from "../store/api/dispatchApi";
import {
  useGetServicesByPoIdQuery,
  useDeleteServiceMutation,
} from "../store/api/serviceApi";
import {
  useGetAccountingEntriesByPoIdQuery,
  useDeleteAccountingEntryMutation,
} from "../store/api/accountingApi";
import type { ServiceResponse, AccountingEntryResponse } from "@OrianaTypes";
import { updatePO } from "../store/poSlice";
import type { ColumnsType } from "antd/es/table";
import type { POItem, POData } from "../store/poSlice";
import type { DispatchResponse } from "@OrianaTypes";
import DispatchFormModal from "../Components/POManagement/DispatchFormModal";
import DispatchDocumentFormModal from "../Components/POManagement/DispatchDocumentFormModal";
import DeliveryConfirmationFormModal from "../Components/POManagement/DeliveryConfirmationFormModal";
import ServiceFormModal from "../Components/POManagement/ServiceFormModal";
import CommissioningFormModal from "../Components/POManagement/CommissioningFormModal";
import WarrantyCertificateFormModal from "../Components/POManagement/WarrantyCertificateFormModal";
import DispatchDetailsModal, {
  DispatchDetailsTab,
} from "../Components/POManagement/DispatchDetailsModal";
import ServiceDetailsModal, {
  ServiceDetailsTab,
} from "../Components/POManagement/ServiceDetailsModal";
import UploadedFilesList from "../Components/POManagement/UploadedFilesList";
import { useGetEntityFilesQuery } from "../store/api/fileApi";
import AddPaymentModal from "../Components/POManagement/AddPaymentModal";
import AddExpenseModal from "../Components/POManagement/AddExpenseModal";
import AccountingDetailsModal from "../Components/POManagement/AccountingDetailsModal";
import UpdateAssignDispatchToModal from "../Components/POManagement/UpdateAssignDispatchToModal";
import UpdateAssignServiceToModal from "../Components/POManagement/UpdateAssignServiceToModal";
import {
  getPaymentStatusColor,
  getPoStatusColor,
  getAccordionStatusColor,
  formatLabel,
} from "../utils";
import { useToast } from "../hooks/useToast";

const { Title } = Typography;
const { Panel } = Collapse;

const PODetails: React.FC = () => {
  const toast = useToast();
  const { poId } = useParams<{ poId: string }>();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectAuth);

  // Fetch PO from API using poId from route params
  const {
    data: poResponse,
    isLoading: isPOLoading,
    isError: isPOError,
    error: poError,
  } = useGetPOByIdQuery(poId || "", {
    skip: !poId, // Skip if poId is not available
  });

  const [updatePOStatus, { isLoading: isClosingPO }] = useUpdatePOMutation();

  // Check if current user is assigned to this PO for dispatch
  const isAssignedToCurrentUser = useMemo(() => {
    return (
      currentUser.userId !== null &&
      currentUser.userId !== undefined &&
      poResponse?.assignDispatchTo !== null &&
      poResponse?.assignDispatchTo !== undefined &&
      currentUser.userId === poResponse.assignDispatchTo
    );
  }, [currentUser.userId, poResponse?.assignDispatchTo]);

  // Check if current user is assigned to this PO for service
  const isAssignedToCurrentUserForService = useMemo(() => {
    return (
      currentUser.userId !== null &&
      currentUser.userId !== undefined &&
      poResponse?.assignServiceTo !== null &&
      poResponse?.assignServiceTo !== undefined &&
      currentUser.userId === poResponse.assignServiceTo
    );
  }, [currentUser.userId, poResponse?.assignServiceTo]);

  // ─── Read permissions (control accordion unlock) ───────────────────────────
  const canReadPO = usePermission(PERMISSIONS.PO_READ);
  const canReadDispatch = usePermission(PERMISSIONS.DISPATCH_READ);
  const canReadDispatchDocument = usePermission(
    PERMISSIONS.DISPATCH_DOCUMENT_READ,
  );
  const canReadDelivery = usePermission(PERMISSIONS.DELIVERY_READ);
  const canReadPreCommissioning = usePermission(
    PERMISSIONS.PRE_COMMISSIONING_READ,
  );
  const canReadCommissioning = usePermission(PERMISSIONS.COMMISSIONING_READ);
  const canReadWarranty = usePermission(PERMISSIONS.WARRANTY_READ);

  // ─── Dispatch Details permissions (assignDispatchTo gate applies) ──────────
  const canCreateDispatch =
    usePermission(PERMISSIONS.DISPATCH_CREATE) && isAssignedToCurrentUser;
  const canUpdateDispatch =
    usePermission(PERMISSIONS.DISPATCH_UPDATE) && isAssignedToCurrentUser;
  const canDeleteDispatch =
    usePermission(PERMISSIONS.DISPATCH_DELETE) && isAssignedToCurrentUser;

  // ─── Dispatch Document permissions (assignDispatchTo gate applies) ─────────
  const canCreateDispatchDocument =
    usePermission(PERMISSIONS.DISPATCH_DOCUMENT_CREATE) &&
    isAssignedToCurrentUser;
  const canUpdateDispatchDocument =
    usePermission(PERMISSIONS.DISPATCH_DOCUMENT_UPDATE) &&
    isAssignedToCurrentUser;
  const canDeleteDispatchDocument =
    usePermission(PERMISSIONS.DISPATCH_DOCUMENT_DELETE) &&
    isAssignedToCurrentUser;

  // ─── Delivery permissions (assignDispatchTo gate applies) ─────────────────
  const canCreateDelivery =
    usePermission(PERMISSIONS.DELIVERY_CREATE) && isAssignedToCurrentUser;
  const canUpdateDelivery =
    usePermission(PERMISSIONS.DELIVERY_UPDATE) && isAssignedToCurrentUser;

  // ─── Pre-Commissioning permissions (assignServiceTo gate applies) ──────────
  const canCreatePreCommissioning =
    usePermission(PERMISSIONS.PRE_COMMISSIONING_CREATE) &&
    isAssignedToCurrentUserForService;
  const canUpdatePreCommissioning =
    usePermission(PERMISSIONS.PRE_COMMISSIONING_UPDATE) &&
    isAssignedToCurrentUserForService;
  const canDeletePreCommissioning =
    usePermission(PERMISSIONS.PRE_COMMISSIONING_DELETE) &&
    isAssignedToCurrentUserForService;

  // ─── Final Commissioning permissions (assignServiceTo gate applies) ────────
  const canUpdateCommissioning =
    usePermission(PERMISSIONS.COMMISSIONING_UPDATE) &&
    isAssignedToCurrentUserForService;

  // ─── Warranty permissions (assignServiceTo gate applies) ──────────────────
  const canUpdateWarranty =
    usePermission(PERMISSIONS.WARRANTY_UPDATE) &&
    isAssignedToCurrentUserForService;

  // ─── Accounting permissions ────────────────────────────────────────────────
  const canReadAccounting = usePermission(PERMISSIONS.ACCOUNTING_READ);
  const canCreateAccounting = usePermission(PERMISSIONS.ACCOUNTING_CREATE);
  const canUpdateAccounting = usePermission(PERMISSIONS.ACCOUNTING_UPDATE);
  const canDeleteAccounting = usePermission(PERMISSIONS.ACCOUNTING_DELETE);

  const canViewPricingAll = usePermission(PERMISSIONS.PO_PRICING_VIEW_ALL);
  const hasUpdateAssignDispatchToPermission = usePermission(
    PERMISSIONS.PO_UPDATE_ASSIGN_DISPATCH_TO,
  );
  const hasUpdateAssignServiceToPermission = usePermission(
    PERMISSIONS.PO_UPDATE_ASSIGN_SERVICE_TO,
  );

  const isCreatedByCurrentUser = useMemo(
    () =>
      currentUser.userId != null &&
      poResponse?.createdById != null &&
      currentUser.userId === poResponse.createdById,
    [currentUser.userId, poResponse?.createdById],
  );

  const canUpdateAssignDispatchTo = useMemo(
    () => hasUpdateAssignDispatchToPermission || isCreatedByCurrentUser,
    [hasUpdateAssignDispatchToPermission, isCreatedByCurrentUser],
  );
  const canUpdateAssignServiceTo = useMemo(
    () => hasUpdateAssignServiceToPermission || isCreatedByCurrentUser,
    [hasUpdateAssignServiceToPermission, isCreatedByCurrentUser],
  );

  // Show pricing columns if user has VIEW_ALL or (VIEW_OWN and PO was created by current user)
  const canViewPricingForThisPO = useMemo(
    () => canViewPricingAll || isCreatedByCurrentUser,
    [canViewPricingAll, isCreatedByCurrentUser],
  );

  const [isDispatchModalVisible, setIsDispatchModalVisible] = useState(false);
  const [editingDispatch, setEditingDispatch] =
    useState<DispatchResponse | null>(null);
  const [isDocumentModalVisible, setIsDocumentModalVisible] = useState(false);
  const [editingDocument, setEditingDocument] =
    useState<DispatchResponse | null>(null);
  const [isDeliveryModalVisible, setIsDeliveryModalVisible] = useState(false);
  const [editingDelivery, setEditingDelivery] =
    useState<DispatchResponse | null>(null);
  const [isPreCommissioningModalVisible, setIsPreCommissioningModalVisible] =
    useState(false);
  const [editingPreCommissioning, setEditingPreCommissioning] =
    useState<ServiceResponse | null>(null);
  const [isCommissioningModalVisible, setIsCommissioningModalVisible] =
    useState(false);
  const [editingCommissioning, setEditingCommissioning] =
    useState<ServiceResponse | null>(null);
  const [isWarrantyModalVisible, setIsWarrantyModalVisible] = useState(false);
  const [editingWarranty, setEditingWarranty] =
    useState<ServiceResponse | null>(null);
  // Accounting: Payment & Expense modals
  const [isPaymentModalVisible, setIsPaymentModalVisible] = useState(false);
  const [isExpenseModalVisible, setIsExpenseModalVisible] = useState(false);
  const [editingAccountingEntry, setEditingAccountingEntry] =
    useState<AccountingEntryResponse | null>(null);
  const [isAccountingDetailsVisible, setIsAccountingDetailsVisible] = useState(false);
  const [viewingAccountingEntry, setViewingAccountingEntry] =
    useState<AccountingEntryResponse | null>(null);

  // Unified Dispatch Details View Modal
  const [isDispatchDetailsModalVisible, setIsDispatchDetailsModalVisible] =
    useState(false);
  const [viewingDispatchDetails, setViewingDispatchDetails] =
    useState<DispatchResponse | null>(null);
  const [dispatchDetailsTab, setDispatchDetailsTab] =
    useState<DispatchDetailsTab>("dispatch");
  // Unified Service Details View Modal
  const [isServiceDetailsModalVisible, setIsServiceDetailsModalVisible] =
    useState(false);
  const [viewingServiceDetails, setViewingServiceDetails] =
    useState<ServiceResponse | null>(null);
  const [serviceDetailsTab, setServiceDetailsTab] =
    useState<ServiceDetailsTab>("precommissioning");
  // Update Assign Dispatch To Modal
  const [
    isUpdateAssignDispatchToModalVisible,
    setIsUpdateAssignDispatchToModalVisible,
  ] = useState(false);
  // Update Assign Service To Modal
  const [
    isUpdateAssignServiceToModalVisible,
    setIsUpdateAssignServiceToModalVisible,
  ] = useState(false);

  // Fetch dispatches from API - only when PO is successfully loaded
  const {
    data: currentPODispatches = [],
  } = useGetDispatchesByPoIdQuery(poId || "", {
    skip: !poId || isPOLoading || isPOError,
  });

  // Delete dispatch mutation
  const [deleteDispatch] = useDeleteDispatchMutation();
  const [updateDispatchDocuments] = useUpdateDispatchDocumentsMutation();

  // Fetch all services for this PO - only when PO is successfully loaded
  const { data: servicesData = [] } = useGetServicesByPoIdQuery(poId || "", {
    skip: !poId || isPOLoading || isPOError,
  });

  // Delete mutation for services
  const [deleteService] = useDeleteServiceMutation();

  // Fetch accounting entries for this PO
  const { data: accountingEntries = [] } = useGetAccountingEntriesByPoIdQuery(
    { poId: poId || "" },
    { skip: !poId || isPOLoading || isPOError },
  );
  const [deleteAccountingEntry] = useDeleteAccountingEntryMutation();

  // Map API response to POData format expected by the component
  const selectedPO: POData | null = useMemo(() => {
    if (!poResponse) return null;

    return {
      id: poResponse.poId,
      clientName: poResponse.clientName || "",
      osgPiNo: poResponse.osgPiNo,
      osgPiDate: poResponse.osgPiDate,
      clientPoNo: poResponse.clientPoNo,
      clientPoDate: poResponse.clientPoDate,
      poStatus: poResponse.poStatus,
      noOfDispatch: poResponse.noOfDispatch,
      assignDispatchTo: 0, // Not used in this component anymore
      assignedUserName: poResponse.assignedUserName,
      clientAddress: poResponse.clientAddress,
      clientContact: poResponse.clientContact,
      clientGST: poResponse.clientGST,
      poItems: poResponse.poItems.map(
        (item): POItem => ({
          id: item.id,
          productId: item.productId,
          category: item.categoryName || "",
          oemName: item.oemName || "",
          product: item.productName || "",
          quantity: item.quantity,
          spareQuantity: item.spareQuantity,
          totalQuantity: item.totalQuantity,
          pricePerUnit: item.pricePerUnit,
          totalPrice: item.totalPrice,
          gstPercent: item.gstPercent,
          finalPrice: item.finalPrice,
          warranty: item.warranty,
        }),
      ),
      dispatchPlanDate: poResponse.dispatchPlanDate,
      siteLocation: poResponse.siteLocation,
      oscSupport: poResponse.oscSupport,
      paymentStatus: poResponse.paymentStatus,
      remarks: poResponse.remarks || "",
      createdAt: poResponse.createdAt,
    };
  }, [poResponse]);

  // PO documents uploaded during Create PO
  const { data: poDocFilesRes, isLoading: isLoadingPoDocs } =
    useGetEntityFilesQuery(
      { entityType: "po_document", entityId: selectedPO?.id ?? "" },
      { skip: !selectedPO?.id },
    );
  const poDocFiles = poDocFilesRes?.data ?? [];

  // ─── Accounting handlers ───────────────────────────────────────────────────
  const handleAddPayment = () => {
    setEditingAccountingEntry(null);
    setIsPaymentModalVisible(true);
  };

  const handleAddExpense = () => {
    setEditingAccountingEntry(null);
    setIsExpenseModalVisible(true);
  };

  const handleEditPaymentEntry = (record: AccountingEntryResponse) => {
    setEditingAccountingEntry(record);
    setIsPaymentModalVisible(true);
  };

  const handleEditExpenseEntry = (record: AccountingEntryResponse) => {
    setEditingAccountingEntry(record);
    setIsExpenseModalVisible(true);
  };

  const handleViewAccountingEntry = (record: AccountingEntryResponse) => {
    setViewingAccountingEntry(record);
    setIsAccountingDetailsVisible(true);
  };

  const handleDeleteAccountingEntry = async (id: number) => {
    try {
      await deleteAccountingEntry({ id, poId: poId || "" }).unwrap();
    } catch (error) {
      console.error("Failed to delete accounting entry:", error);
    }
  };

  const handleEditDispatch = (record: DispatchResponse) => {
    setEditingDispatch(record);
    setIsDispatchModalVisible(true);
  };

  const handleDeleteDispatch = async (dispatchId: number) => {
    if (!poId) return;
    try {
      await deleteDispatch({ id: dispatchId, poId }).unwrap();
    } catch (error) {
      console.error("Failed to delete dispatch:", error);
    }
  };

  // Delete document details - clears document related fields from dispatch
  const handleDeleteDocumentDetails = async (dispatchId: number) => {
    try {
      // Clear all document fields by updating with empty values
      await updateDispatchDocuments({
        id: dispatchId,
        data: {
          noDuesClearance: undefined,
          docOsgPiNo: undefined,
          docOsgPiDate: undefined,
          taxInvoiceNumber: undefined,
          invoiceDate: undefined,
          ewayBill: undefined,
          deliveryChallan: undefined,
          dispatchDate: undefined,
          packagingList: undefined,
          deliveryPincode: undefined,
          dispatchStatus: undefined,
          dispatchLrNo: undefined,
          dispatchRemarks: undefined,
          serialNumbers: {},
        },
      }).unwrap();
    } catch (error) {
      console.error("Failed to clear document details:", error);
    }
  };

  const handleCloseModal = () => {
    setIsDispatchModalVisible(false);
    setEditingDispatch(null);
  };

  const handleAddDispatch = () => {
    setEditingDispatch(null);
    setIsDispatchModalVisible(true);
  };

  // Unified view handler for dispatch details modal
  const handleViewDispatchDetails = (
    record: DispatchResponse,
    tab: DispatchDetailsTab,
  ) => {
    setViewingDispatchDetails(record);
    setDispatchDetailsTab(tab);
    setIsDispatchDetailsModalVisible(true);
  };

  const handleCloseDispatchDetailsModal = () => {
    setIsDispatchDetailsModalVisible(false);
    setViewingDispatchDetails(null);
  };

  // Unified view handler for service details modal
  const handleViewServiceDetails = (
    record: ServiceResponse,
    tab: ServiceDetailsTab,
  ) => {
    setViewingServiceDetails(record);
    setServiceDetailsTab(tab);
    setIsServiceDetailsModalVisible(true);
  };

  const handleCloseServiceDetailsModal = () => {
    setIsServiceDetailsModalVisible(false);
    setViewingServiceDetails(null);
  };

  // Document modal handlers
  const handleAddDocument = () => {
    setEditingDocument(null);
    setIsDocumentModalVisible(true);
  };

  const handleEditDocument = (record: DispatchResponse) => {
    setEditingDocument(record);
    setIsDocumentModalVisible(true);
  };

  const handleCloseDocumentModal = () => {
    setIsDocumentModalVisible(false);
    setEditingDocument(null);
  };

  // Get dispatches with documents for the table (check if dispatchStatus exists)
  const dispatchesWithDocuments = useMemo(() => {
    return currentPODispatches.filter((d) => !!d.dispatchStatus);
  }, [currentPODispatches]);

  // Get dispatches eligible for delivery confirmation (dispatchStatus === "done")
  const dispatchesForDeliveryConfirmation = useMemo(() => {
    return currentPODispatches.filter((d) => d.dispatchStatus === "done");
  }, [currentPODispatches]);

  // Get dispatches with delivery confirmation for the table (check if deliveryStatus exists)
  const dispatchesWithDeliveryConfirmation = useMemo(() => {
    return currentPODispatches.filter((d) => !!d.deliveryStatus);
  }, [currentPODispatches]);

  // Delivery confirmation modal handlers
  const handleAddDeliveryConfirmation = () => {
    setEditingDelivery(null);
    setIsDeliveryModalVisible(true);
  };

  const handleEditDeliveryConfirmation = (record: DispatchResponse) => {
    setEditingDelivery(record);
    setIsDeliveryModalVisible(true);
  };

  const handleCloseDeliveryModal = () => {
    setIsDeliveryModalVisible(false);
    setEditingDelivery(null);
  };

  // Get dispatches eligible for pre-commissioning (deliveryStatus === "done")
  const dispatchesForPreCommissioning = useMemo(() => {
    return currentPODispatches.filter((d) => d.deliveryStatus === "done");
  }, [currentPODispatches]);

  // Process services data - all services appear in all three tables
  // All services (appear in all accordion tables)
  const preCommissioningServices = useMemo(() => {
    return servicesData;
  }, [servicesData]);

  // Commissioning services (all services - fields show '-' if not available)
  const commissioningServices = useMemo(() => {
    return servicesData;
  }, [servicesData]);

  // Warranty services (all services - fields show '-' if not available)
  const warrantyServices = useMemo(() => {
    return servicesData;
  }, [servicesData]);

  // Validation helper functions for service lifecycle
  const canEditPreCommissioning = (record: ServiceResponse): boolean => {
    // Must be assigned to current user for service
    if (!isAssignedToCurrentUserForService) {
      return false;
    }
    // Cannot edit if commissioning is already done (reverse validation)
    if (record.commissioningStatus?.toLowerCase() === "done") {
      return false;
    }
    return true;
  };

  const canEditCommissioning = (record: ServiceResponse): boolean => {
    // Must be assigned to current user for service
    if (!isAssignedToCurrentUserForService) {
      return false;
    }
    // Must have pre-commissioning done (forward validation)
    if (record.preCommissioningStatus?.toLowerCase() !== "done") {
      return false;
    }
    // Cannot edit if warranty is already done (reverse validation)
    if (record.warrantyStatus?.toLowerCase() === "done") {
      return false;
    }
    return true;
  };

  const canEditWarranty = (record: ServiceResponse): boolean => {
    // Must be assigned to current user for service
    if (!isAssignedToCurrentUserForService) {
      return false;
    }
    // Must have commissioning done (forward validation)
    return record.commissioningStatus?.toLowerCase() === "done";
  };

  // Pre-commissioning modal handlers
  const handleAddPreCommissioning = () => {
    setEditingPreCommissioning(null);
    setIsPreCommissioningModalVisible(true);
  };

  const handleEditPreCommissioning = (record: ServiceResponse) => {
    setEditingPreCommissioning(record);
    setIsPreCommissioningModalVisible(true);
  };

  const handleDeletePreCommissioningRecord = async (serviceId: number) => {
    if (!poId) return;
    try {
      // Find the service to get dispatchId for cache invalidation
      const service = servicesData.find((s) => s.serviceId === serviceId);
      await deleteService({
        id: serviceId,
        dispatchId: service?.dispatchId,
        poId,
      }).unwrap();
    } catch (error) {
      console.error("Failed to delete service:", error);
    }
  };

  const handleClosePreCommissioningModal = () => {
    setIsPreCommissioningModalVisible(false);
    setEditingPreCommissioning(null);
  };

  // Commissioning modal handlers
  const handleEditCommissioning = (record: ServiceResponse) => {
    setEditingCommissioning(record);
    setIsCommissioningModalVisible(true);
  };

  const handleCloseCommissioningModal = () => {
    setIsCommissioningModalVisible(false);
    setEditingCommissioning(null);
  };

  // ============= ACCORDION STATUS FROM PO RESPONSE =============

  type AccordionStatus = "Not Started" | "In-Progress" | "Done";

  // Get accordion status from PO response
  const accordionStatus = poResponse?.accordionStatus;

  // Dispatch Status Info - from PO response
  const dispatchStatusInfo = useMemo(() => {
    if (!accordionStatus?.dispatch) {
      return {
        status: "Not Started" as AccordionStatus,
        totalQty: 0,
        dispatchedQty: 0,
      };
    }
    return {
      status: accordionStatus.dispatch.status as AccordionStatus,
      totalQty: accordionStatus.dispatch.totalQty,
      dispatchedQty: accordionStatus.dispatch.dispatchedQty,
    };
  }, [accordionStatus]);

  // Document Status - from PO response
  const documentStatus = useMemo((): AccordionStatus => {
    return (accordionStatus?.document?.status ||
      "Not Started") as AccordionStatus;
  }, [accordionStatus]);

  // Delivery Confirmation Status - from PO response
  const deliveryConfirmationStatus = useMemo((): AccordionStatus => {
    return (accordionStatus?.delivery?.status ||
      "Not Started") as AccordionStatus;
  }, [accordionStatus]);

  // Pre-Commissioning Status - from PO response
  const preCommissioningAccordionStatus = useMemo((): AccordionStatus => {
    return (accordionStatus?.preCommissioning?.status ||
      "Not Started") as AccordionStatus;
  }, [accordionStatus]);

  // Commissioning Status - from PO response
  const commissioningAccordionStatus = useMemo((): AccordionStatus => {
    return (accordionStatus?.commissioning?.status ||
      "Not Started") as AccordionStatus;
  }, [accordionStatus]);

  // Warranty Status - from PO response
  const warrantyAccordionStatus = useMemo((): AccordionStatus => {
    return (accordionStatus?.warranty?.status ||
      "Not Started") as AccordionStatus;
  }, [accordionStatus]);

  // sumOfPaymentMade = total of paymentAmount (+ tds for part payments) across all payment entries
  // totalBalance = totalPoCost - sumOfPaymentMade
  const { sumOfPaymentMade, totalBalance } = useMemo(() => {
    const totalPoCost = poResponse?.totalPoCost ?? 0;
    const sum = accountingEntries.reduce((s, e) => {
      const type = e.paymentType?.toLowerCase();
      const amount = e.paymentAmount ? parseFloat(e.paymentAmount) : 0;
      const tds = e.tds ? parseFloat(e.tds) : 0;
      if (!type || amount <= 0) return s;
      if (type === "part_payment") {
        return s + amount + tds;
      }
      // advance / credit_note
      return s + amount;
    }, 0);
    return {
      sumOfPaymentMade: sum,
      totalBalance: totalPoCost - sum,
    };
  }, [accountingEntries, poResponse?.totalPoCost]);

  // Accounting Status: Done when balance is 0 and no dues is yes/approved for all entries
  const accountingAccordionStatus = useMemo((): AccordionStatus => {
    if (!accountingEntries.length) return "Not Started";
    // Balance is totalPoCost - sumOfPaymentMade; Done when it is zero
    const balanceIsZero = totalBalance === 0;
    // All entries with noDuesClearanceStatus must be yes or approved
    const entriesWithNoDues = accountingEntries.filter(
      (e) =>
        e.noDuesClearanceStatus != null &&
        String(e.noDuesClearanceStatus).trim() !== "",
    );
    const allNoDuesApproved =
      entriesWithNoDues.length === 0 ||
      entriesWithNoDues.every(
        (e) =>
          e.noDuesClearanceStatus?.toLowerCase() === "yes" ||
          e.noDuesClearanceStatus?.toLowerCase() === "approved",
      );
    if (balanceIsZero && allNoDuesApproved) return "Done";
    return "In-Progress";
  }, [accountingEntries, totalBalance]);

  // Check if all accordions are in "Done" state
  const allAccordionsDone = useMemo(() => {
    return (
      dispatchStatusInfo.status === "Done" &&
      documentStatus === "Done" &&
      deliveryConfirmationStatus === "Done" &&
      preCommissioningAccordionStatus === "Done" &&
      commissioningAccordionStatus === "Done" &&
      warrantyAccordionStatus === "Done"
    );
  }, [
    dispatchStatusInfo.status,
    documentStatus,
    deliveryConfirmationStatus,
    preCommissioningAccordionStatus,
    commissioningAccordionStatus,
    warrantyAccordionStatus,
  ]);

  // Check if PO is already closed
  const isPOClosed = selectedPO?.poStatus === "closed";

  // Handle Close PO
  const handleClosePO = async () => {
    if (!selectedPO) return;
    if (!allAccordionsDone) {
      toast.error("Cannot close PO", {
        description: "Please complete all sections before closing this PO.",
      });
      return;
    }
    if (isPOClosed) return;

    try {
      const updated = await updatePOStatus({
        poId: selectedPO.id,
        poStatus: "closed",
      }).unwrap();

      dispatch(
        updatePO({
          ...selectedPO,
          poStatus: updated?.poStatus ?? "closed",
        }),
      );

      toast.success("PO closed successfully");
    } catch (error: unknown) {
      const errorMessage =
        error && typeof error === "object" && "data" in error
          ? (error.data as { message?: string })?.message
          : error instanceof Error
            ? error.message
            : "Failed to close PO. Please try again.";

      toast.error("Failed to close PO", { description: errorMessage });
    }
  };

  // Helper function to render accordion header with status and optional lock icon
  const renderAccordionHeader = (
    title: string,
    status: string,
    isLocked = false,
    counts?: { done: number; total: number; unit?: string },
  ) => {
    const allDone = counts ? counts.done >= counts.total && counts.total > 0 : false;
    const showCounts = counts && counts.total > 0;
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {title}
          {isLocked && (
            <Tooltip title="You don't have permission to access this section">
              <LockOutlined style={{ color: "#ff4d4f", fontSize: 14 }} />
            </Tooltip>
          )}
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 8, marginRight: "16px" }}>
          {showCounts && (
            <Tooltip
              title={`${counts!.done} of ${counts!.total} ${counts!.unit ?? "dispatches"} completed`}
            >
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: allDone ? "#065f46" : "#92400e",
                  background: allDone ? "#11998e14" : "#f7971e14",
                  border: `1px solid ${allDone ? "#11998e35" : "#f7971e35"}`,
                  borderRadius: 20,
                  padding: "2px 9px",
                  letterSpacing: "0.2px",
                  cursor: "default",
                }}
              >
                {counts!.done}/{counts!.total} {counts!.unit ?? "dispatches"}
              </span>
            </Tooltip>
          )}
          <Tag
            color={getAccordionStatusColor(status)}
            style={{ marginLeft: 0, marginBottom: 0 }}
          >
            {status}
          </Tag>
        </span>
      </div>
    );
  };

  // Warranty modal handlers
  const handleEditWarranty = (record: ServiceResponse) => {
    setEditingWarranty(record);
    setIsWarrantyModalVisible(true);
  };

  const handleCloseWarrantyModal = () => {
    setIsWarrantyModalVisible(false);
    setEditingWarranty(null);
  };

  const itemColumns: ColumnsType<POItem> = useMemo(() => {
    const PRICING_COLUMN_KEYS = ["pricePerUnit", "totalPrice", "finalPrice"];
    const allColumns: ColumnsType<POItem> = [
      {
        title: "Category",
        dataIndex: "category",
        key: "category",
        render: (value) => formatLabel(value || ""),
      },
      {
        title: "OEM Name",
        dataIndex: "oemName",
        key: "oemName",
        render: (value) => formatLabel(value || ""),
      },
      {
        title: "Product",
        dataIndex: "product",
        key: "product",
        render: (value) => formatLabel(value || ""),
      },
      {
        title: "Quantity",
        dataIndex: "quantity",
        key: "quantity",
      },
      {
        title: "Spare Qty",
        dataIndex: "spareQuantity",
        key: "spareQuantity",
      },
      {
        title: "Total Qty",
        dataIndex: "totalQuantity",
        key: "totalQuantity",
      },
      {
        title: "Price/Unit",
        dataIndex: "pricePerUnit",
        key: "pricePerUnit",
        render: (value) =>
          `₹${value?.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || 0}`,
      },
      {
        title: "Total Price",
        dataIndex: "totalPrice",
        key: "totalPrice",
        render: (value) =>
          `₹${value?.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || 0}`,
      },
      {
        title: "GST %",
        dataIndex: "gstPercent",
        key: "gstPercent",
        render: (value) => (value ? `${value}%` : "-"),
      },
      {
        title: "Final Price",
        dataIndex: "finalPrice",
        key: "finalPrice",
        render: (value) =>
          value
            ? `₹${value?.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : "-",
      },
      {
        title: "Warranty",
        dataIndex: "warranty",
        key: "warranty",
        render: (value) => formatLabel(value || ""),
      },
    ];
    return canViewPricingForThisPO
      ? allColumns
      : allColumns.filter(
          (col) => !PRICING_COLUMN_KEYS.includes((col.key as string) || ""),
        );
  }, [canViewPricingForThisPO]);

  const dispatchColumns: ColumnsType<DispatchResponse> = [
    {
      title: "Dispatch ID",
      dataIndex: "dispatchId",
      key: "dispatchId",
      width: 100,
      fixed: "left",
      render: (value: number) => <Tag color="blue">#{value}</Tag>,
    },
    {
      title: "Dispatched Date",
      dataIndex: "confirmDispatchDate",
      key: "confirmDispatchDate",
      width: 130,
      fixed: "left",
    },
    {
      title: "Project Name",
      dataIndex: "projectName",
      key: "projectName",
      width: 180,
    },
    {
      title: "Project Location",
      dataIndex: "projectLocation",
      key: "projectLocation",
      width: 180,
    },
    {
      title: "Dispatched Items",
      dataIndex: "dispatchedItems",
      key: "dispatchedItems",
      width: 250,
      render: (items: DispatchResponse["dispatchedItems"]) =>
        items?.map((item, index) => (
          <div key={index}>
            <Tag color="blue">
              {formatLabel(item.productName || "")}: {item.quantity}
            </Tag>
          </div>
        )) || "-",
    },
    {
      title: "Contact",
      dataIndex: "deliveryContact",
      key: "deliveryContact",
      width: 150,
      fixed: "left",
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 120,
      render: (_, record) => {
        // Disable edit if dispatch status is "done" or no permission
        const isDispatchDone = record.dispatchStatus === "done";
        const isEditDisabled = isDispatchDone || !canUpdateDispatch;
        const editDisabledReason = !canUpdateDispatch
          ? "You don't have permission to edit dispatches"
          : isDispatchDone
            ? "Editing disabled - Dispatch status is Done"
            : "Edit";
        // Disable delete if dispatch status is "done" OR delivery status exists OR no permission
        const hasDeliveryStatus = !!record.deliveryStatus;
        const isDeleteDisabled =
          isDispatchDone || hasDeliveryStatus || !canDeleteDispatch;
        const deleteDisabledReason = !canDeleteDispatch
          ? "You don't have permission to delete dispatches"
          : hasDeliveryStatus
            ? "Delete disabled - Delivery confirmation exists"
            : isDispatchDone
              ? "Delete disabled - Dispatch status is Done"
              : "Delete";
        return (
          <Space size="small">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => handleViewDispatchDetails(record, "dispatch")}
              style={{ padding: 0, color: "#1890ff" }}
              title="View Details"
            />
            <Tooltip title={editDisabledReason}>
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => handleEditDispatch(record)}
                style={{ padding: 0 }}
                disabled={isEditDisabled}
              />
            </Tooltip>
            <Popconfirm
              title="Delete Dispatch"
              description="Are you sure you want to delete this dispatch? This will remove all dispatch details data."
              onConfirm={() => handleDeleteDispatch(record.dispatchId)}
              okText="Yes"
              cancelText="No"
              okButtonProps={{ danger: true }}
              disabled={isDeleteDisabled}
            >
              <Tooltip title={deleteDisabledReason}>
                <Button
                  type="link"
                  danger
                  icon={<DeleteOutlined />}
                  style={{ padding: 0 }}
                  disabled={isDeleteDisabled}
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  // Dispatch Document table columns (flat properties)
  const documentColumns: ColumnsType<DispatchResponse> = [
    {
      title: "Dispatch ID",
      dataIndex: "dispatchId",
      key: "dispatchId",
      width: 100,
      fixed: "left",
      render: (value: number) => <Tag color="blue">#{value}</Tag>,
    },
    {
      title: "Dispatched Items",
      dataIndex: "dispatchedItems",
      key: "dispatchedItems",
      width: 180,
      fixed: "left",
      render: (items: DispatchResponse["dispatchedItems"]) =>
        items?.map((item, index) => (
          <div key={index}>
            <Tag color="geekblue">
              {formatLabel(item.productName || "")}: {item.quantity}
            </Tag>
          </div>
        )) || "-",
    },
    {
      title: "Dispatch Date",
      dataIndex: "dispatchDate",
      key: "dispatchDate",
      width: 120,
    },
    {
      title: "No Due Clearance",
      dataIndex: "noDuesClearance",
      key: "noDuesClearance",
      width: 140,
      render: (value: string) => {
        const colorMap: Record<string, string> = {
          pending: "orange",
          approved: "green",
          rejected: "red",
          on_hold: "blue",
        };
        return (
          <Tag color={colorMap[value] || "default"}>
            {formatLabel(value || "")}
          </Tag>
        );
      },
    },
    {
      title: "Tax Invoice No",
      dataIndex: "taxInvoiceNumber",
      key: "taxInvoiceNumber",
      width: 140,
    },
    {
      title: "Invoice Date",
      dataIndex: "invoiceDate",
      key: "invoiceDate",
      width: 120,
    },
    {
      title: "E-way Bill",
      dataIndex: "ewayBill",
      key: "ewayBill",
      width: 130,
    },
    {
      title: "Dispatch Status",
      dataIndex: "dispatchStatus",
      key: "dispatchStatus",
      width: 130,
      render: (value: string) => {
        const colorMap: Record<string, string> = {
          done: "green",
          pending: "orange",
          hold: "blue",
          cancelled: "red",
        };
        return (
          <Tag color={colorMap[value] || "default"}>
            {formatLabel(value || "")}
          </Tag>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 120,
      render: (_, record) => {
        const isDeliveryDone = record.deliveryStatus === "done";
        const hasDeliveryConfirmation = !!record.deliveryStatus;
        const isEditDisabled = isDeliveryDone || !canUpdateDispatchDocument;
        const editDisabledReason = !canUpdateDispatchDocument
          ? "You don't have permission to edit documents"
          : isDeliveryDone
            ? "Editing disabled - Delivery is done"
            : "Edit";
        const isDeleteDisabled =
          hasDeliveryConfirmation || !canDeleteDispatchDocument;
        const deleteDisabledReason = !canDeleteDispatchDocument
          ? "You don't have permission to delete documents"
          : hasDeliveryConfirmation
            ? "Delete disabled - Delivery confirmation exists"
            : "Delete";
        return (
          <Space size="small">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => handleViewDispatchDetails(record, "documents")}
              style={{ padding: 0, color: "#1890ff" }}
              title="View Details"
            />
            <Tooltip title={editDisabledReason}>
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => handleEditDocument(record)}
                style={{ padding: 0 }}
                disabled={isEditDisabled}
              />
            </Tooltip>
            <Popconfirm
              title="Delete Document Details"
              description="Are you sure you want to delete this document details? This will remove all document data."
              onConfirm={() => handleDeleteDocumentDetails(record.dispatchId)}
              okText="Yes"
              cancelText="No"
              okButtonProps={{ danger: true }}
              disabled={isDeleteDisabled}
            >
              <Tooltip title={deleteDisabledReason}>
                <Button
                  type="link"
                  danger
                  icon={<DeleteOutlined />}
                  style={{ padding: 0 }}
                  disabled={isDeleteDisabled}
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  // Delivery Confirmation table columns (flat properties)
  const deliveryConfirmationColumns: ColumnsType<DispatchResponse> = [
    {
      title: "Dispatch ID",
      dataIndex: "dispatchId",
      key: "dispatchId",
      width: 100,
      fixed: "left",
      render: (value: number) => <Tag color="blue">#{value}</Tag>,
    },
    {
      title: "Dispatched Items",
      dataIndex: "dispatchedItems",
      key: "dispatchedItems",
      width: 200,
      render: (items: DispatchResponse["dispatchedItems"]) =>
        items?.map((item, index) => (
          <div key={index}>
            <Tag color="blue">
              {formatLabel(item.productName || "")}: {item.quantity}
            </Tag>
          </div>
        )) || "-",
    },
    {
      title: "Date of Delivery",
      dataIndex: "dateOfDelivery",
      key: "dateOfDelivery",
      width: 140,
    },
    {
      title: "Delivery Status",
      dataIndex: "deliveryStatus",
      key: "deliveryStatus",
      width: 140,
      render: (value: string) => {
        const colorMap: Record<string, string> = {
          done: "green",
          pending: "orange",
          hold: "blue",
          cancelled: "red",
        };
        return (
          <Tag color={colorMap[value] || "default"}>
            {formatLabel(value || "")}
          </Tag>
        );
      },
    },
    {
      title: "Proof of Delivery",
      dataIndex: "proofOfDelivery",
      key: "proofOfDelivery",
      width: 250,
      ellipsis: true,
    },
    {
      title: "Updated At",
      dataIndex: "deliveryUpdatedAt",
      key: "deliveryUpdatedAt",
      width: 180,
      render: (value: string) =>
        value ? new Date(value).toLocaleString() : "-",
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 100,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewDispatchDetails(record, "delivery")}
            style={{ padding: 0, color: "#1890ff" }}
            title="View Details"
          />
          <Tooltip
            title={
              canUpdateDelivery
                ? "Edit"
                : "You don't have permission to edit delivery"
            }
          >
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEditDeliveryConfirmation(record)}
              style={{ padding: 0 }}
              disabled={!canUpdateDelivery}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Pre-Commissioning table columns
  const preCommissioningColumns: ColumnsType<ServiceResponse> = [
    {
      title: "ID",
      dataIndex: "serviceId",
      key: "serviceId",
      width: 60,
      fixed: "left",
    },
    {
      title: "Dispatch ID",
      dataIndex: "dispatchId",
      key: "dispatchId",
      width: 100,
      render: (value: number) => <Tag color="blue">#{value}</Tag>,
    },
    {
      title: "Serial Number",
      dataIndex: "serialNumber",
      key: "serialNumber",
      width: 140,
    },
    {
      title: "Product",
      dataIndex: "productName",
      key: "productName",
      width: 120,
      render: (value: string) => formatLabel(value || ""),
    },
    {
      title: "PC Contact",
      dataIndex: "pcContact",
      key: "pcContact",
      width: 140,
    },
    {
      title: "Service Engineer",
      dataIndex: "assignedServiceEngineer",
      key: "assignedServiceEngineer",
      width: 150,
    },
    {
      title: "PPM Ticket no",
      dataIndex: "ppmTickedNoFromOem",
      key: "ppmTickedNoFromOem",
      width: 130,
      ellipsis: true,
    },
    {
      title: "PPM Confirmation",
      dataIndex: "ppmConfirmationStatus",
      key: "ppmConfirmationStatus",
      width: 140,
      render: (value: string) => {
        const colorMap: Record<string, string> = {
          pending: "orange",
          confirmed: "green",
          rejected: "red",
          in_progress: "blue",
        };
        return (
          <Tag color={colorMap[value] || "default"}>
            {formatLabel(value || "")}
          </Tag>
        );
      },
    },
    {
      title: "Pre-comm. Status",
      dataIndex: "preCommissioningStatus",
      key: "preCommissioningStatus",
      width: 140,
      render: (value: string) => {
        const colorMap: Record<string, string> = {
          done: "green",
          pending: "orange",
          hold: "blue",
          cancelled: "red",
        };
        return (
          <Tag color={colorMap[value?.toLowerCase()] || "default"}>
            {formatLabel(value || "-")}
          </Tag>
        );
      },
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      width: 150,
      ellipsis: true,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewServiceDetails(record, "precommissioning")}
            style={{ padding: 0, color: "#1890ff" }}
            title="View Details"
          />
          <Tooltip
            title={
              !isAssignedToCurrentUserForService
                ? "You are not assigned to handle services for this PO"
                : !canUpdatePreCommissioning
                  ? "You don't have permission to edit pre-commissioning"
                  : !canEditPreCommissioning(record)
                    ? "Cannot edit pre-commissioning: Commissioning is already completed"
                    : "Edit"
            }
          >
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEditPreCommissioning(record)}
              style={{ padding: 0 }}
              disabled={
                !canUpdatePreCommissioning || !canEditPreCommissioning(record)
              }
            />
          </Tooltip>
          <Popconfirm
            title="Delete Pre-Commissioning"
            description="Are you sure you want to delete this entry?"
            onConfirm={() =>
              handleDeletePreCommissioningRecord(record.serviceId)
            }
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
            disabled={!canDeletePreCommissioning}
          >
            <Tooltip
              title={
                canDeletePreCommissioning
                  ? "Delete"
                  : "You don't have permission to delete pre-commissioning"
              }
            >
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                style={{ padding: 0 }}
                disabled={!canDeletePreCommissioning}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Commissioning table columns
  const commissioningColumns: ColumnsType<ServiceResponse> = [
    {
      title: "ID",
      dataIndex: "serviceId",
      key: "serviceId",
      width: 60,
      fixed: "left",
    },
    {
      title: "Dispatch ID",
      dataIndex: "dispatchId",
      key: "dispatchId",
      width: 100,
      render: (value: number) => <Tag color="blue">#{value}</Tag>,
    },
    {
      title: "Serial Number",
      dataIndex: "serialNumber",
      key: "serialNumber",
      width: 140,
    },
    {
      title: "Product",
      dataIndex: "productName",
      key: "productName",
      width: 120,
      render: (value: string) => formatLabel(value || ""),
    },
    {
      title: "ECD from Client",
      dataIndex: "commissioningEcdFromClient",
      key: "commissioningEcdFromClient",
      width: 150,
      render: (value: string) => value || "-",
    },
    {
      title: "Service Ticket No",
      dataIndex: "commissioningServiceTicketNo",
      key: "commissioningServiceTicketNo",
      width: 150,
      render: (value: string) => value || "-",
    },
    {
      title: "Issues",
      dataIndex: "commissioningIssues",
      key: "commissioningIssues",
      width: 150,
      ellipsis: true,
      render: (value: string) => value || "-",
    },
    {
      title: "Solution",
      dataIndex: "commissioningSolution",
      key: "commissioningSolution",
      width: 150,
      ellipsis: true,
      render: (value: string) => value || "-",
    },
    {
      title: "Comm. Date",
      dataIndex: "commissioningDate",
      key: "commissioningDate",
      width: 120,
      render: (value: string) => value || "-",
    },
    {
      title: "Comm. Status",
      dataIndex: "commissioningStatus",
      key: "commissioningStatus",
      width: 130,
      render: (value: string) => {
        const colorMap: Record<string, string> = {
          done: "green",
          pending: "orange",
          hold: "blue",
          cancelled: "red",
        };
        return (
          <Tag color={colorMap[value?.toLowerCase()] || "default"}>
            {formatLabel(value || "-")}
          </Tag>
        );
      },
    },
    {
      title: "Remarks",
      dataIndex: "commissioningRemarks",
      key: "commissioningRemarks",
      width: 150,
      ellipsis: true,
      render: (value: string) => value || "-",
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 100,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewServiceDetails(record, "commissioning")}
            style={{ padding: 0, color: "#1890ff" }}
            title="View Details"
          />
          <Tooltip
            title={
              !isAssignedToCurrentUserForService
                ? "You are not assigned to handle services for this PO"
                : !canUpdateCommissioning
                  ? "You don't have permission to edit commissioning"
                  : !canEditCommissioning(record)
                    ? record.preCommissioningStatus?.toLowerCase() !== "done"
                      ? "Cannot edit commissioning: Pre-commissioning must be completed first"
                      : "Cannot edit commissioning: Warranty is already completed"
                    : "Edit"
            }
          >
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEditCommissioning(record)}
              style={{ padding: 0 }}
              disabled={
                !canUpdateCommissioning || !canEditCommissioning(record)
              }
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Warranty Certificate table columns
  const warrantyColumns: ColumnsType<ServiceResponse> = [
    {
      title: "ID",
      dataIndex: "serviceId",
      key: "serviceId",
      width: 60,
      fixed: "left",
    },
    {
      title: "Dispatch ID",
      dataIndex: "dispatchId",
      key: "dispatchId",
      width: 100,
      render: (value: number) => <Tag color="blue">#{value}</Tag>,
    },
    {
      title: "Serial Number",
      dataIndex: "serialNumber",
      key: "serialNumber",
      width: 140,
    },
    {
      title: "Product",
      dataIndex: "productName",
      key: "productName",
      width: 120,
      render: (value: string) => formatLabel(value || ""),
    },
    {
      title: "Certificate No",
      dataIndex: "warrantyCertificateNo",
      key: "warrantyCertificateNo",
      width: 150,
      render: (value: string) => value || "-",
    },
    {
      title: "Issue Date",
      dataIndex: "warrantyIssueDate",
      key: "warrantyIssueDate",
      width: 120,
      render: (value: string) => value || "-",
    },
    {
      title: "Start Date",
      dataIndex: "warrantyStartDate",
      key: "warrantyStartDate",
      width: 120,
      render: (value: string) => value || "-",
    },
    {
      title: "End Date",
      dataIndex: "warrantyEndDate",
      key: "warrantyEndDate",
      width: 120,
      render: (value: string) => value || "-",
    },
    {
      title: "Warranty Status",
      dataIndex: "warrantyStatus",
      key: "warrantyStatus",
      width: 130,
      render: (value: string) => {
        const colorMap: Record<string, string> = {
          done: "green",
          pending: "orange",
          hold: "blue",
          cancelled: "red",
        };
        return (
          <Tag color={colorMap[value?.toLowerCase()] || "default"}>
            {formatLabel(value || "-")}
          </Tag>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 100,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewServiceDetails(record, "warranty")}
            style={{ padding: 0, color: "#1890ff" }}
            title="View Details"
          />
          <Tooltip
            title={
              !isAssignedToCurrentUserForService
                ? "You are not assigned to handle services for this PO"
                : !canUpdateWarranty
                  ? "You don't have permission to edit warranty"
                  : !canEditWarranty(record)
                    ? "Cannot edit warranty: Commissioning must be completed first"
                    : "Edit"
            }
          >
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEditWarranty(record)}
              style={{ padding: 0 }}
              disabled={!canUpdateWarranty || !canEditWarranty(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Show loading state
  if (isPOLoading) {
    return (
      <div
        style={{
          padding: "1rem",
          background: "#fff",
          minHeight: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Spin size="large" tip="Loading PO details..." />
      </div>
    );
  }

  // Show error state
  if (isPOError) {
    return (
      <div
        style={{
          padding: "1rem",
          background: "#fff",
          minHeight: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Alert
          message="Error loading PO details"
          description={
            poError && typeof poError === "object" && "data" in poError
              ? (poError.data as any)?.message || "Failed to load PO details"
              : "Failed to load PO details. Please try again."
          }
          type="error"
          showIcon
        />
      </div>
    );
  }

  // Show empty state if PO not found
  if (!selectedPO) {
    return (
      <div
        style={{
          padding: "1rem",
          background: "#fff",
          minHeight: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Empty description="PO not found. Please select a valid PO from the dashboard." />
      </div>
    );
  }

  return (
    <div
      style={{
        padding: "1rem",
        background: "#fff",
        minHeight: "100%",
      }}
    >
      {/* Page Header - Vibrant & Modern */}
      <motion.div
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
        style={{
          background: "linear-gradient(135deg, #667eea08 0%, #764ba208 100%)",
          borderRadius: 16,
          border: `1px solid ${colors.gray200}`,
          borderLeft: `4px solid ${colors.accent}`,
          boxShadow: shadows.card,
          padding: "24px 28px",
          marginBottom: 24,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 16,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 8px 24px rgba(102, 126, 234, 0.35)",
            }}
          >
            <FileTextOutlined style={{ fontSize: 26, color: "#fff" }} />
          </div>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <h2
                style={{
                  margin: 0,
                  fontSize: "1.5rem",
                  fontWeight: 700,
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  letterSpacing: "-0.02em",
                }}
              >
                Order Tracking
              </h2>
              <span
                style={{
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "#fff",
                  padding: "4px 12px",
                  borderRadius: 8,
                  fontSize: "0.8rem",
                  fontWeight: 600,
                }}
              >
                {selectedPO.id}
              </span>
              <Tag
                color={isPOClosed ? "error" : "success"}
                style={{
                  fontSize: 12,
                  padding: "3px 10px",
                  fontWeight: 500,
                  borderRadius: 6,
                  border: "none",
                }}
              >
                {isPOClosed ? "Closed" : "Active"}
              </Tag>
            </div>
            <p
              style={{
                margin: "6px 0 0 0",
                fontSize: "0.9rem",
                color: colors.gray500,
              }}
            >
              View and manage order details, dispatches & commissioning
            </p>
          </div>
        </div>
        <Popconfirm
          title="Close PO"
          description="Are you sure you want to close this PO? This action cannot be undone."
          onConfirm={handleClosePO}
          okText="Yes, Close PO"
          cancelText="Cancel"
          okButtonProps={{ danger: true, loading: isClosingPO }}
          disabled={!allAccordionsDone || isPOClosed || isClosingPO}
        >
          <Button
            type="primary"
            danger
            icon={<LockOutlined />}
            loading={isClosingPO}
            disabled={!allAccordionsDone || isPOClosed || isClosingPO}
            style={{
              borderRadius: 8,
              fontWeight: 500,
              height: 40,
              padding: "0 20px",
            }}
            title={
              isPOClosed
                ? "PO is already closed"
                : !allAccordionsDone
                  ? "Complete all sections before closing PO"
                  : "Close this PO"
            }
          >
            {isPOClosed ? "PO Closed" : "Close PO"}
          </Button>
        </Popconfirm>
      </motion.div>

      <Collapse defaultActiveKey={["1"]} accordion={false}>
        {/* PO Information Accordion */}
        <Panel
          header={renderAccordionHeader("PO Information", "", !canReadPO)}
          key="1"
          collapsible={!canReadPO ? "disabled" : undefined}
        >
          <Descriptions
            bordered
            column={{ xxl: 3, xl: 3, lg: 2, md: 2, sm: 1, xs: 1 }}
            size="small"
            labelStyle={{ fontWeight: 600, backgroundColor: "#fafafa" }}
          >
            <Descriptions.Item label="OSG Order ID">
              <Tag color="blue">{selectedPO.id}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Client Name">
              {selectedPO.clientName}
            </Descriptions.Item>
            <Descriptions.Item label="OSG PI No">
              {selectedPO.osgPiNo}
            </Descriptions.Item>
            <Descriptions.Item label="OSG PI Date">
              {selectedPO.osgPiDate}
            </Descriptions.Item>
            <Descriptions.Item label="Client PO No">
              {selectedPO.clientPoNo}
            </Descriptions.Item>
            <Descriptions.Item label="Client PO Date">
              {selectedPO.clientPoDate}
            </Descriptions.Item>
            <Descriptions.Item label="PO Status">
              <Tag color={getPoStatusColor(selectedPO.poStatus)}>
                {formatLabel(selectedPO.poStatus)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="No of Dispatch">
              <Tag color="cyan">{formatLabel(selectedPO.noOfDispatch)}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Client Address" span={2}>
              {selectedPO.clientAddress}
            </Descriptions.Item>
            <Descriptions.Item label="Client Contact">
              {selectedPO.clientContact}
            </Descriptions.Item>
            <Descriptions.Item label="Client GST No.">
              {selectedPO.clientGST || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Assign Dispatch To">
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span>{selectedPO.assignedUserName || "Not Assigned"}</span>
                {canUpdateAssignDispatchTo ? (
                  <Button
                    type="link"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() =>
                      setIsUpdateAssignDispatchToModalVisible(true)
                    }
                    style={{ padding: 0, height: "auto" }}
                  >
                    Update
                  </Button>
                ) : (
                  <Tooltip title="You need permission and must be the PO creator to update Assign Dispatch To">
                    <Button
                      type="link"
                      size="small"
                      icon={<EditOutlined />}
                      disabled
                      style={{ padding: 0, height: "auto" }}
                    >
                      Update
                    </Button>
                  </Tooltip>
                )}
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="Assign Service To">
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span>
                  {poResponse?.assignedServiceUserName || "Not Assigned"}
                </span>
                {canUpdateAssignServiceTo ? (
                  <Button
                    type="link"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => setIsUpdateAssignServiceToModalVisible(true)}
                    style={{ padding: 0, height: "auto" }}
                  >
                    Update
                  </Button>
                ) : (
                  <Tooltip title="You need permission and must be the PO creator to update Assign Service To">
                    <Button
                      type="link"
                      size="small"
                      icon={<EditOutlined />}
                      disabled
                      style={{ padding: 0, height: "auto" }}
                    >
                      Update
                    </Button>
                  </Tooltip>
                )}
              </div>
            </Descriptions.Item>

            <Descriptions.Item label="Dispatch Plan Date">
              {selectedPO.dispatchPlanDate}
            </Descriptions.Item>
            <Descriptions.Item label="Site Location">
              {selectedPO.siteLocation}
            </Descriptions.Item>
            <Descriptions.Item label="OSC Support">
              <Tag color={selectedPO.oscSupport === "yes" ? "green" : "orange"}>
                {formatLabel(selectedPO.oscSupport)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Payment Status">
              <Tag color={getPaymentStatusColor(selectedPO.paymentStatus)}>
                {formatLabel(selectedPO.paymentStatus)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Total PO Cost">
              <span style={{ fontWeight: 700, fontSize: "15px", color: "#71a241" }}>
                ₹{" "}
                {(poResponse?.totalPoCost ?? 0).toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Remarks" span={3}>
              {selectedPO.remarks}
            </Descriptions.Item>
            <Descriptions.Item label="Created At">
              {selectedPO.createdAt}
            </Descriptions.Item>
            <Descriptions.Item label="Created by">
              {poResponse?.createdByName || "N/A"}
            </Descriptions.Item>
          </Descriptions>

          {/* Item Details Table */}
          {selectedPO.poItems && selectedPO.poItems.length > 0 && (
            <div style={{ marginTop: "1.5rem" }}>
              <Title level={5} style={{ marginBottom: "1rem" }}>
                Item Details
              </Title>
              <Table
                columns={itemColumns}
                dataSource={selectedPO.poItems.map((item, index) => ({
                  ...item,
                  key: index,
                }))}
                pagination={false}
                bordered
                size="small"
                scroll={{ x: 900 }}
              />
            </div>
          )}

          {/* Uploaded documents (from Create PO) */}
          <div style={{ marginTop: "1.5rem" }}>
            <UploadedFilesList
              files={poDocFiles}
              isLoading={isLoadingPoDocs}
              title="Documents uploaded during Create PO"
            />
          </div>
        </Panel>

        {/* Accounting Accordion */}
        <Panel
          header={renderAccordionHeader(
            "Accounting",
            accountingAccordionStatus,
            !canReadAccounting,
          )}
          key="accounting"
          collapsible={!canReadAccounting ? "disabled" : undefined}
        >
          {/* Summary KPI Cards */}
          {(() => {
            const totalPoCost = poResponse?.totalPoCost ?? 0;
            const totalAdvance = accountingEntries.reduce(
              (s, e) =>
                e.paymentType === "advance" && e.paymentAmount
                  ? s + parseFloat(e.paymentAmount)
                  : s,
              0,
            );
            const totalDispatchExp = accountingEntries.reduce(
              (s, e) => s + (e.dispatchExpenses ? parseFloat(e.dispatchExpenses) : 0),
              0,
            );
            const totalCommExp = accountingEntries.reduce(
              (s, e) => s + (e.commissioningExpenses ? parseFloat(e.commissioningExpenses) : 0),
              0,
            );
            // totalBalance = totalPoCost - sumOfPaymentMade (computed in useMemo above)
            const fmt = (n: number) =>
              n.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              });
            const cardBase: React.CSSProperties = {
              borderRadius: "14px",
              padding: "16px 18px",
              position: "relative",
              overflow: "hidden",
              boxShadow:
                "0 4px 24px 0 rgba(60,60,100,0.10), 0 1.5px 6px 0 rgba(60,60,100,0.06)",
              backdropFilter: "blur(8px)",
              flex: 1,
              minWidth: "160px",
            };
            const cards = [
              {
                label: "Total PO Cost",
                value: totalPoCost,
                icon: "📋",
                gradient: "linear-gradient(135deg, #1a1d2e 0%, #2d3561 100%)",
                subtext: `${selectedPO?.poItems?.length ?? 0} line items`,
              },
              {
                label: "Advance Amount",
                value: totalAdvance,
                icon: "💰",
                gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                subtext: `${accountingEntries.filter((e) => e.paymentType === "advance").length} entries`,
              },
              {
                label: "Dispatch Expense",
                value: totalDispatchExp,
                icon: "🚚",
                gradient: "linear-gradient(135deg, #f85f7a 0%, #fc466b 100%)",
                subtext: `${accountingEntries.filter((e) => e.dispatchExpenses).length} entries`,
              },
              {
                label: "Commission Exp.",
                value: totalCommExp,
                icon: "⚙️",
                gradient: "linear-gradient(135deg, #f7971e 0%, #ffd200 100%)",
                subtext: `${accountingEntries.filter((e) => e.commissioningExpenses).length} entries`,
              },
              {
                label: "Balance Amount",
                value: totalBalance,
                icon: "⚖️",
                gradient: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                subtext: `Total PO Cost − Payments (₹${sumOfPaymentMade.toLocaleString("en-IN", { minimumFractionDigits: 2 })})`,
              },
            ];
            return (
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  flexWrap: "nowrap",
                  marginBottom: "24px",
                  overflowX: "auto",
                  paddingBottom: "4px",
                }}
              >
                {cards.map((card) => (
                  <div
                    key={card.label}
                    style={{
                      ...cardBase,
                      background: card.gradient,
                    }}
                  >
                    {/* Decorative circles */}
                    <div
                      style={{
                        position: "absolute",
                        top: "-14px",
                        right: "-14px",
                        width: "64px",
                        height: "64px",
                        borderRadius: "50%",
                        background: "rgba(255,255,255,0.10)",
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        bottom: "-22px",
                        right: "16px",
                        width: "80px",
                        height: "80px",
                        borderRadius: "50%",
                        background: "rgba(255,255,255,0.06)",
                      }}
                    />
                    <div style={{ fontSize: "22px", marginBottom: "6px", lineHeight: 1 }}>
                      {card.icon}
                    </div>
                    <div
                      style={{
                        color: "rgba(255,255,255,0.80)",
                        fontSize: "10px",
                        fontWeight: 600,
                        letterSpacing: "0.7px",
                        textTransform: "uppercase",
                        marginBottom: "3px",
                      }}
                    >
                      {card.label}
                    </div>
                    <div
                      style={{
                        color: "#fff",
                        fontSize: "18px",
                        fontWeight: 800,
                        letterSpacing: "-0.3px",
                        lineHeight: 1.2,
                      }}
                    >
                      ₹ {fmt(card.value)}
                    </div>
                    <div
                      style={{
                        color: "rgba(255,255,255,0.65)",
                        fontSize: "10px",
                        marginTop: "5px",
                      }}
                    >
                      {card.subtext}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* PO info strip (prefilled from PO) */}
          <div
            style={{
              display: "flex",
              gap: 12,
              flexWrap: "wrap",
              marginBottom: 16,
              padding: "12px 16px",
              background: "linear-gradient(135deg, #667eea12 0%, #764ba208 100%)",
              border: "1px solid #667eea25",
              borderRadius: 10,
            }}
          >
            <div style={{ flex: 1, minWidth: 160 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#4c51bf", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 4 }}>
                Tax Invoice No. (OSG PI No)
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#1e1b4b" }}>
                {selectedPO?.osgPiNo || "—"}
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 140 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#4c51bf", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 4 }}>
                Tax Invoice Date (OSG PI Date)
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#1e1b4b" }}>
                {selectedPO?.osgPiDate
                  ? new Date(selectedPO.osgPiDate).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })
                  : "—"}
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 140 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#4c51bf", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 4 }}>
                Invoice Amount (Total PO Cost)
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#1e1b4b" }}>
                ₹{(poResponse?.totalPoCost ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
              </div>
            </div>
          </div>

          {/* Add Payment / Add Expense buttons */}
          <div
            style={{
              display: "flex",
              gap: 12,
              justifyContent: "flex-end",
              marginBottom: "1rem",
              flexWrap: "wrap",
            }}
          >
            {canCreateAccounting ? (
              <>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddPayment}
                  style={{
                    background: colors.primary,
                    borderRadius: 8,
                    fontWeight: 600,
                    border: "none",
                  }}
                >
                  Add Payment
                </Button>
                <Tooltip
                  title={
                    currentPODispatches.length === 0
                      ? "Add at least one dispatch to record expenses"
                      : undefined
                  }
                >
                  <span>
                    <Button
                      type="default"
                      icon={<PlusOutlined />}
                      onClick={handleAddExpense}
                      disabled={currentPODispatches.length === 0}
                      style={{ borderRadius: 8, fontWeight: 600 }}
                    >
                      Add Expense
                    </Button>
                  </span>
                </Tooltip>
              </>
            ) : (
              <>
                <Tooltip title="You don't have permission to create accounting entries">
                  <Button type="primary" icon={<PlusOutlined />} disabled style={{ borderRadius: 8, fontWeight: 600 }}>
                    Add Payment
                  </Button>
                </Tooltip>
                <Tooltip title="You don't have permission to create accounting entries">
                  <Button type="default" icon={<PlusOutlined />} disabled style={{ borderRadius: 8, fontWeight: 600 }}>
                    Add Expense
                  </Button>
                </Tooltip>
              </>
            )}
          </div>

          {/* Payments table */}
          {(() => {
            const paymentEntries = accountingEntries.filter(
              (e) =>
                // Any entry with a paymentType is considered a payment row
                !!e.paymentType
            );
            const paymentTypeLabel = (r: AccountingEntryResponse) => {
              const type = r.paymentType?.toLowerCase();
              if (type === "advance") return "Advance";
              if (type === "part_payment") return "Part Payment";
              if (type === "credit_note") return "Credit Note";
              return "Payment";
            };
            const paymentAmount = (r: AccountingEntryResponse) =>
              r.paymentAmount && parseFloat(r.paymentAmount) > 0 ? r.paymentAmount : "—";
            const fmt = (v: string) =>
              v && v !== "—"
                ? `₹ ${parseFloat(v).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
                : "—";
            return (
              <>
                <Typography.Title level={5} style={{ marginBottom: 8, marginTop: 8 }}>
                  Payments
                </Typography.Title>
                {paymentEntries.length > 0 ? (
                  <div style={{ overflowX: "auto", marginBottom: 24 }}>
                    <Table
                      columns={[
                        {
                          title: "#",
                          dataIndex: "id",
                          key: "id",
                          width: 56,
                          render: (v: number) => <Tag color="purple">#{v}</Tag>,
                        },
                        {
                          title: "Type",
                          key: "type",
                          width: 110,
                          render: (_: unknown, r: AccountingEntryResponse) => (
                            <Tag
                              color={
                                paymentTypeLabel(r) === "Advance"
                                  ? "blue"
                                  : paymentTypeLabel(r) === "Part Payment"
                                  ? "green"
                                  : "purple"
                              }
                            >
                              {paymentTypeLabel(r)}
                            </Tag>
                          ),
                        },
                        {
                          title: "Dispatch",
                          dataIndex: "dispatchId",
                          key: "dispatchId",
                          width: 90,
                          render: (v: number | undefined) =>
                            v != null ? (
                              <Tag color="blue">#{v}</Tag>
                            ) : (
                              "—"
                            ),
                        },
                        {
                          title: "Amount",
                          key: "amount",
                          width: 120,
                          render: (_: unknown, r: AccountingEntryResponse) => fmt(paymentAmount(r)),
                        },
                        {
                          title: "TDS",
                          dataIndex: "tds",
                          key: "tds",
                          width: 90,
                          render: (v: string) => fmt(v ?? ""),
                        },
                        {
                          title: "Balance",
                          dataIndex: "balanceAmount",
                          key: "balanceAmount",
                          width: 110,
                          render: (v: string) =>
                            v ? (
                              <span style={{ color: "#22c55e", fontWeight: 600 }}>
                                ₹ {parseFloat(v).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                              </span>
                            ) : (
                              "—"
                            ),
                        },
                        {
                          title: "No Dues",
                          dataIndex: "noDuesClearanceStatus",
                          key: "noDuesClearanceStatus",
                          width: 90,
                          render: (v: string) => {
                            if (!v) return "—";
                            const colorMap: Record<string, string> = {
                              yes: "green",
                              no: "red",
                              pending: "orange",
                            };
                            return (
                              <Tag color={colorMap[v] || "default"}>
                                {v.charAt(0).toUpperCase() + v.slice(1)}
                              </Tag>
                            );
                          },
                        },
                        {
                          title: "Ref No.",
                          key: "refNo",
                          width: 140,
                          ellipsis: true,
                          render: (_: unknown, r: AccountingEntryResponse) =>
                            paymentTypeLabel(r) === "Credit Note"
                              ? (r.creditNoteRef || "—")
                              : (r.multiplePaymentRefNo || "—"),
                        },
                        {
                          title: "Date",
                          key: "date",
                          width: 100,
                          render: (_: unknown, r: AccountingEntryResponse) => {
                            const d = r.paymentDate || "";
                            return d
                              ? new Date(d).toLocaleDateString("en-IN", {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                })
                              : "—";
                          },
                        },
                        {
                          title: "Actions",
                          key: "actions",
                          fixed: "right" as const,
                          width: 110,
                          render: (_: unknown, record: AccountingEntryResponse) => (
                            <Space size="small">
                              <Tooltip title="View">
                                <Button
                                  type="text"
                                  icon={<EyeOutlined />}
                                  size="small"
                                  onClick={() => handleViewAccountingEntry(record)}
                                />
                              </Tooltip>
                              {canUpdateAccounting && (
                                <Tooltip title="Edit">
                                  <Button
                                    type="text"
                                    icon={<EditOutlined />}
                                    size="small"
                                    onClick={() => handleEditPaymentEntry(record)}
                                  />
                                </Tooltip>
                              )}
                              {canDeleteAccounting && (
                                <Popconfirm
                                  title="Delete this payment entry?"
                                  onConfirm={() => handleDeleteAccountingEntry(record.id)}
                                  okText="Delete"
                                  okButtonProps={{ danger: true }}
                                >
                                  <Tooltip title="Delete">
                                    <Button type="text" icon={<DeleteOutlined />} size="small" danger />
                                  </Tooltip>
                                </Popconfirm>
                              )}
                            </Space>
                          ),
                        },
                      ]}
                      dataSource={paymentEntries.map((e) => ({ ...e, key: e.id }))}
                      pagination={false}
                      bordered
                      size="small"
                      scroll={{ x: 950 }}
                      tableLayout="fixed"
                    />
                  </div>
                ) : (
                  <Empty description="No payments yet. Click 'Add Payment' to add advance, part payment or credit note." style={{ marginBottom: 24 }} />
                )}

                {/* Expenses table */}
                {(() => {
                  const expenseEntries = accountingEntries.filter(
                    (e) =>
                      (e.dispatchExpenses && parseFloat(e.dispatchExpenses) > 0) ||
                      (e.commissioningExpenses && parseFloat(e.commissioningExpenses) > 0)
                  );
                  const expenseType = (r: AccountingEntryResponse) =>
                    r.dispatchExpenses && parseFloat(r.dispatchExpenses) > 0
                      ? "Dispatch"
                      : "Commissioning";
                  const expenseAmount = (r: AccountingEntryResponse) =>
                    r.dispatchExpenses && parseFloat(r.dispatchExpenses) > 0
                      ? r.dispatchExpenses
                      : r.commissioningExpenses ?? "—";
                  const expenseRemark = (r: AccountingEntryResponse) =>
                    r.dispatchExpensesRemark || r.commissioningExpensesRemark || "—";
                  return (
                    <>
                      <Typography.Title level={5} style={{ marginBottom: 8 }}>
                        Expenses
                      </Typography.Title>
                      {expenseEntries.length > 0 ? (
                        <div style={{ overflowX: "auto" }}>
                          <Table
                            columns={[
                              {
                                title: "#",
                                dataIndex: "id",
                                key: "id",
                                width: 56,
                                render: (v: number) => <Tag color="orange">#{v}</Tag>,
                              },
                              {
                                title: "Type",
                                key: "type",
                                width: 180,
                                render: (_: unknown, r: AccountingEntryResponse) => (
                                  <Tag color={expenseType(r) === "Dispatch" ? "red" : "gold"}>
                                    {expenseType(r)} expense
                                  </Tag>
                                ),
                              },
                              {
                                title: "Dispatch / Service",
                                key: "link",
                                width: 140,
                                render: (_: unknown, r: AccountingEntryResponse) =>
                                  r.dispatchId
                                    ? `Dispatch #${r.dispatchId}`
                                    : r.serviceId
                                    ? `Service #${r.serviceId}`
                                    : "—",
                              },
                              {
                                title: "Amount",
                                key: "amount",
                                width: 120,
                                render: (_: unknown, r: AccountingEntryResponse) =>
                                  expenseAmount(r) !== "—"
                                    ? `₹ ${parseFloat(expenseAmount(r)).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
                                    : "—",
                              },
                              {
                                title: "Remark",
                                key: "remark",
                                width: 140,
                                ellipsis: true,
                                render: (_: unknown, r: AccountingEntryResponse) => expenseRemark(r),
                              },
                              {
                                title: "Actions",
                                key: "actions",
                                fixed: "right" as const,
                                width: 110,
                                render: (_: unknown, record: AccountingEntryResponse) => (
                                  <Space size="small">
                                    <Tooltip title="View">
                                      <Button
                                        type="text"
                                        icon={<EyeOutlined />}
                                        size="small"
                                        onClick={() => handleViewAccountingEntry(record)}
                                      />
                                    </Tooltip>
                                    {canUpdateAccounting && (
                                      <Tooltip title="Edit">
                                        <Button
                                          type="text"
                                          icon={<EditOutlined />}
                                          size="small"
                                          onClick={() => handleEditExpenseEntry(record)}
                                        />
                                      </Tooltip>
                                    )}
                                    {canDeleteAccounting && (
                                      <Popconfirm
                                        title="Delete this expense entry?"
                                        onConfirm={() => handleDeleteAccountingEntry(record.id)}
                                        okText="Delete"
                                        okButtonProps={{ danger: true }}
                                      >
                                        <Tooltip title="Delete">
                                          <Button type="text" icon={<DeleteOutlined />} size="small" danger />
                                        </Tooltip>
                                      </Popconfirm>
                                    )}
                                  </Space>
                                ),
                              },
                            ]}
                            dataSource={expenseEntries.map((e) => ({ ...e, key: e.id }))}
                            pagination={false}
                            bordered
                            size="small"
                            scroll={{ x: 750 }}
                            tableLayout="fixed"
                          />
                        </div>
                      ) : (
                        <Empty description="No expenses yet. Click 'Add Expense' to add dispatch or commissioning expense (requires at least one dispatch)." />
                      )}
                    </>
                  );
                })()}
              </>
            );
          })()}
        </Panel>

        {/* Dispatch Details Accordion */}
        <Panel
          header={renderAccordionHeader(
            "Dispatch Details",
            dispatchStatusInfo.status,
            !canReadDispatch,
          )}
          key="2"
          collapsible={!canReadDispatch ? "disabled" : undefined}
        >
          {/* Add Dispatch Button */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: "1rem",
            }}
          >
            {canCreateDispatch ? (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddDispatch}
                style={{
                  background: colors.primary,
                  borderRadius: 8,
                  fontWeight: 600,
                  border: "none",
                }}
              >
                Add Dispatch
              </Button>
            ) : (
              <Tooltip title="You don't have permission to create dispatches">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  disabled
                  style={{
                    borderRadius: 8,
                    fontWeight: 600,
                  }}
                >
                  Add Dispatch
                </Button>
              </Tooltip>
            )}
          </div>

          {/* Dispatch Details Table or Empty State */}
          {currentPODispatches.length > 0 ? (
            <div style={{ overflowX: "auto" }}>
              <Table
                columns={dispatchColumns}
                dataSource={currentPODispatches.map((dispatchItem) => ({
                  ...dispatchItem,
                  key: dispatchItem.dispatchId,
                }))}
                pagination={false}
                bordered
                size="small"
                scroll={{ x: 900 }}
                tableLayout="fixed"
              />
            </div>
          ) : (
            <Empty description="No dispatch details available" />
          )}
        </Panel>

        {/* Dispatch Document Accordion */}
        <Panel
          header={renderAccordionHeader(
            "Dispatch Document",
            documentStatus,
            !canReadDispatchDocument,
            {
              done: accordionStatus?.document?.completed ?? 0,
              total: currentPODispatches.length,
              unit: "dispatches",
            },
          )}
          key="3"
          collapsible={!canReadDispatchDocument ? "disabled" : undefined}
        >
          {/* Update Document Details Button */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: "1rem",
            }}
          >
            {canCreateDispatchDocument ? (
              <Button
                type="primary"
                icon={<FileTextOutlined />}
                onClick={handleAddDocument}
                style={{
                  background: colors.primary,
                  borderRadius: 8,
                  fontWeight: 600,
                  border: "none",
                }}
              >
                Update Document Details
              </Button>
            ) : (
              <Tooltip title="You don't have permission to update dispatch documents">
                <Button
                  type="primary"
                  icon={<FileTextOutlined />}
                  disabled
                  style={{
                    borderRadius: 8,
                    fontWeight: 600,
                  }}
                >
                  Update Document Details
                </Button>
              </Tooltip>
            )}
          </div>

          {/* Dispatch Documents Table or Empty State */}
          {dispatchesWithDocuments.length > 0 ? (
            <div style={{ overflowX: "auto" }}>
              <Table
                columns={documentColumns}
                dataSource={dispatchesWithDocuments.map((dispatchItem) => ({
                  ...dispatchItem,
                  key: dispatchItem.dispatchId,
                }))}
                pagination={false}
                bordered
                size="small"
                scroll={{ x: 1400 }}
                tableLayout="fixed"
              />
            </div>
          ) : (
            <Empty description="No dispatch documents available" />
          )}
        </Panel>

        {/* Delivery Confirmation Accordion */}
        <Panel
          header={renderAccordionHeader(
            "Delivery Confirmation",
            deliveryConfirmationStatus,
            !canReadDelivery,
            {
              done: accordionStatus?.delivery?.completed ?? 0,
              total: currentPODispatches.length,
              unit: "dispatches",
            },
          )}
          key="4"
          collapsible={!canReadDelivery ? "disabled" : undefined}
        >
          {/* Update Delivery Information Button */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: "1rem",
            }}
          >
            {canCreateDelivery ? (
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={handleAddDeliveryConfirmation}
                disabled={dispatchesForDeliveryConfirmation.length === 0}
                style={{
                  background: colors.primary,
                  borderRadius: 8,
                  fontWeight: 600,
                  border: "none",
                }}
              >
                Update Delivery Information
              </Button>
            ) : (
              <Tooltip title="You don't have permission to update delivery information">
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  disabled
                  style={{
                    borderRadius: 8,
                    fontWeight: 600,
                  }}
                >
                  Update Delivery Information
                </Button>
              </Tooltip>
            )}
          </div>

          {/* Delivery Confirmation Table or Empty State */}
          {dispatchesWithDeliveryConfirmation.length > 0 ? (
            <div style={{ overflowX: "auto" }}>
              <Table
                columns={deliveryConfirmationColumns}
                dataSource={dispatchesWithDeliveryConfirmation.map(
                  (dispatchItem) => ({
                    ...dispatchItem,
                    key: dispatchItem.dispatchId,
                  }),
                )}
                pagination={false}
                bordered
                size="small"
                scroll={{ x: 1000 }}
                tableLayout="fixed"
              />
            </div>
          ) : (
            <Empty
              description={
                dispatchesForDeliveryConfirmation.length === 0
                  ? "No dispatches with 'Done' status available for delivery confirmation"
                  : "No delivery confirmations available"
              }
            />
          )}
        </Panel>

        {/* Pre-Commissioning Accordion */}
        <Panel
          header={renderAccordionHeader(
            "Pre-Commissioning",
            preCommissioningAccordionStatus,
            !canReadPreCommissioning,
            {
              done: accordionStatus?.preCommissioning?.completed ?? 0,
              total: accordionStatus?.preCommissioning?.total ?? 0,
              unit: "services",
            },
          )}
          key="5"
          collapsible={!canReadPreCommissioning ? "disabled" : undefined}
        >
          {/* Update Pre-Commissioning Details Button */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: "1rem",
            }}
          >
            {canCreatePreCommissioning ? (
              <Button
                type="primary"
                icon={<ToolOutlined />}
                onClick={handleAddPreCommissioning}
                disabled={dispatchesForPreCommissioning.length === 0}
                style={{
                  background: colors.primary,
                  borderRadius: 8,
                  fontWeight: 600,
                  border: "none",
                }}
              >
                Update Pre-Commissioning Details
              </Button>
            ) : (
              <Tooltip title="You don't have permission to update pre-commissioning details">
                <Button
                  type="primary"
                  icon={<ToolOutlined />}
                  disabled
                  style={{
                    borderRadius: 8,
                    fontWeight: 600,
                  }}
                >
                  Update Pre-Commissioning Details
                </Button>
              </Tooltip>
            )}
          </div>

          {/* Pre-Commissioning Table or Empty State */}
          {preCommissioningServices.length > 0 ? (
            <div style={{ overflowX: "auto" }}>
              <Table
                columns={preCommissioningColumns}
                dataSource={preCommissioningServices.map((s) => ({
                  ...s,
                  key: s.serviceId,
                }))}
                pagination={false}
                bordered
                size="small"
                scroll={{ x: 1500 }}
                tableLayout="fixed"
              />
            </div>
          ) : (
            <Empty
              description={
                dispatchesForPreCommissioning.length === 0
                  ? "No dispatches with 'Done' delivery status available for pre-commissioning"
                  : "No pre-commissioning details available"
              }
            />
          )}
        </Panel>

        {/* Final Commissioning Accordion */}
        <Panel
          header={renderAccordionHeader(
            "Final Commissioning",
            commissioningAccordionStatus,
            !canReadCommissioning,
            {
              done: accordionStatus?.commissioning?.completed ?? 0,
              total: accordionStatus?.commissioning?.total ?? 0,
              unit: "services",
            },
          )}
          key="6"
          collapsible={!canReadCommissioning ? "disabled" : undefined}
        >
          {/* Commissioning Table or Empty State */}
          {commissioningServices.length > 0 ? (
            <div style={{ overflowX: "auto" }}>
              <Table
                columns={commissioningColumns}
                dataSource={commissioningServices.map((s) => ({
                  ...s,
                  key: s.serviceId,
                }))}
                pagination={false}
                bordered
                size="small"
                scroll={{ x: 1600 }}
                tableLayout="fixed"
              />
            </div>
          ) : (
            <Empty
              description={
                servicesData.length === 0
                  ? "No services available. Create a pre-commissioning entry first."
                  : "No services available"
              }
            />
          )}
        </Panel>

        {/* Warranty Certificate Accordion */}
        <Panel
          header={renderAccordionHeader(
            "Warranty Certificate",
            warrantyAccordionStatus,
            !canReadWarranty,
            {
              done: accordionStatus?.warranty?.completed ?? 0,
              total: accordionStatus?.warranty?.total ?? 0,
              unit: "services",
            },
          )}
          key="7"
          collapsible={!canReadWarranty ? "disabled" : undefined}
        >
          {/* Warranty Certificate Table or Empty State */}
          {warrantyServices.length > 0 ? (
            <div style={{ overflowX: "auto" }}>
              <Table
                columns={warrantyColumns}
                dataSource={warrantyServices.map((s) => ({
                  ...s,
                  key: s.serviceId,
                }))}
                pagination={false}
                bordered
                size="small"
                scroll={{ x: 1100 }}
                tableLayout="fixed"
              />
            </div>
          ) : (
            <Empty
              description={
                servicesData.length === 0
                  ? "No services available. Create a pre-commissioning entry first."
                  : "No services available"
              }
            />
          )}
        </Panel>
      </Collapse>

      {/* Dispatch Form Modal */}
      <DispatchFormModal
        visible={isDispatchModalVisible}
        onClose={handleCloseModal}
        poId={selectedPO.id}
        poItems={selectedPO.poItems}
        editData={editingDispatch}
      />

      {/* Dispatch Document Form Modal */}
      <DispatchDocumentFormModal
        visible={isDocumentModalVisible}
        onClose={handleCloseDocumentModal}
        dispatches={currentPODispatches}
        editData={editingDocument}
        poId={poId}
      />

      {/* Delivery Confirmation Form Modal */}
      <DeliveryConfirmationFormModal
        visible={isDeliveryModalVisible}
        onClose={handleCloseDeliveryModal}
        dispatches={dispatchesForDeliveryConfirmation}
        editData={editingDelivery}
        poId={poId}
      />

      {/* Service Form Modal (Pre-Commissioning) */}
      <ServiceFormModal
        visible={isPreCommissioningModalVisible}
        onClose={handleClosePreCommissioningModal}
        poId={poId || ""}
        dispatches={dispatchesForPreCommissioning}
        editData={editingPreCommissioning}
      />

      {/* Commissioning Form Modal */}
      <CommissioningFormModal
        visible={isCommissioningModalVisible}
        onClose={handleCloseCommissioningModal}
        poId={poId || ""}
        editData={editingCommissioning}
      />

      {/* Warranty Certificate Form Modal */}
      <WarrantyCertificateFormModal
        visible={isWarrantyModalVisible}
        onClose={handleCloseWarrantyModal}
        poId={poId || ""}
        editData={editingWarranty}
      />

      {/* Unified Dispatch Details View Modal */}
      <DispatchDetailsModal
        visible={isDispatchDetailsModalVisible}
        onClose={handleCloseDispatchDetailsModal}
        dispatch={viewingDispatchDetails}
        initialTab={dispatchDetailsTab}
      />

      {/* Unified Service Details View Modal */}
      <ServiceDetailsModal
        visible={isServiceDetailsModalVisible}
        onClose={handleCloseServiceDetailsModal}
        data={viewingServiceDetails}
        initialTab={serviceDetailsTab}
      />

      {/* Update Assign Dispatch To Modal */}
      <UpdateAssignDispatchToModal
        visible={isUpdateAssignDispatchToModalVisible}
        onClose={() => setIsUpdateAssignDispatchToModalVisible(false)}
        poId={selectedPO.id}
        currentAssignDispatchTo={poResponse?.assignDispatchTo}
        currentAssignedUserName={poResponse?.assignedUserName}
      />

      {/* Update Assign Service To Modal */}
      <UpdateAssignServiceToModal
        visible={isUpdateAssignServiceToModalVisible}
        onClose={() => setIsUpdateAssignServiceToModalVisible(false)}
        poId={selectedPO.id}
        currentAssignServiceTo={poResponse?.assignServiceTo}
        currentAssignedServiceUserName={poResponse?.assignedServiceUserName}
      />

      {/* Add Payment Modal */}
      <AddPaymentModal
        open={isPaymentModalVisible}
        onClose={() => {
          setIsPaymentModalVisible(false);
          setEditingAccountingEntry(null);
        }}
        poId={selectedPO?.id ?? ""}
        poData={selectedPO ?? undefined}
        dispatches={currentPODispatches}
        editRecord={editingAccountingEntry}
        totalPoCost={poResponse?.totalPoCost ?? 0}
        totalAdvanceReceived={accountingEntries.reduce(
          (s, e) =>
            e.paymentType === "advance" && e.paymentAmount
              ? s + parseFloat(e.paymentAmount)
              : s,
          0
        )}
        previousBalance={totalBalance}
      />

      {/* Add Expense Modal */}
      <AddExpenseModal
        open={isExpenseModalVisible}
        onClose={() => {
          setIsExpenseModalVisible(false);
          setEditingAccountingEntry(null);
        }}
        poId={poId || ""}
        dispatches={currentPODispatches}
        services={servicesData}
        editRecord={editingAccountingEntry}
      />

      {/* Accounting Entry Details Modal */}
      <AccountingDetailsModal
        open={isAccountingDetailsVisible}
        onClose={() => {
          setIsAccountingDetailsVisible(false);
          setViewingAccountingEntry(null);
        }}
        entry={viewingAccountingEntry}
      />
    </div>
  );
};

export default PODetails;
